import React, { useEffect } from 'react';
import { Form, Input } from 'antd';
import CustomModal from '@/components/UI/Modals/CustomModal';

const { TextArea } = Input;

/**
 * AddPermissionModal - Modal for creating, viewing, and editing permissions
 * 
 * Features:
 * - Permission name and description fields
 * - Form validation
 * - Support for create, edit, and view modes
 */
const AddPermissionModal = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  loading = false,
  mode = 'create',
  permissionData = null
}) => {
  const [form] = Form.useForm();
  
  // Determine if fields should be readonly
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  // Populate form when in edit or view mode
  useEffect(() => {
    if (isOpen && permissionData && (isEditMode || isViewMode)) {
      // console.log('Populating permission form with:', permissionData);
      form.setFieldsValue({
        name: permissionData.name,
        description: permissionData.description
      });
    } else if (isOpen && isCreateMode) {
      form.resetFields();
    }
  }, [isOpen, permissionData, mode, form, isEditMode, isViewMode, isCreateMode]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const submitData = {
        name: values.name,
        description: values.description
      };
      
      // Include ID for edit mode
      if (isEditMode && permissionData?.id) {
        submitData.id = permissionData.id;
      }
      
      await onSubmit(submitData);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  // Get modal title based on mode
  const getModalTitle = () => {
    switch (mode) {
      case 'edit':
        return 'Edit Permission';
      case 'view':
        return 'View Permission';
      default:
        return 'Add Permission';
    }
  };

  // Get modal subtitle based on mode
  const getModalSubtitle = () => {
    switch (mode) {
      case 'edit':
        return 'Update the permission details below.';
      case 'view':
        return 'View the permission details below.';
      default:
        return 'Kindly fill in the details below to add a new permission.';
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      title={getModalTitle()}
      subtitle={getModalSubtitle()}
      width={600}
      primaryActionText={isEditMode ? 'Update →' : isViewMode ? null : 'Create →'}
      onPrimaryAction={handleSubmit}
      secondaryActionText="Cancel"
      onSecondaryAction={handleClose}
      hideSecondaryAction={isViewMode}
      primaryActionDisabled={loading}
      loading={loading}
      noActionButton={isViewMode}
      primaryButtonColor="orange"
    >
      <Form form={form}>
        <div className="p-6 space-y-6">
          {/* Permission Details Section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-900">
                Permission name <span className="text-red-500">*</span>
              </label>
              <Form.Item
                name="name"
                rules={[{ required: !isViewMode, message: 'Please enter permission name' }]}
                className="mb-0"
              >
                <Input
                  placeholder="e.g Transaction"
                  size="large"
                  readOnly={isViewMode}
                  // disabled={isViewMode}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ 
                    backgroundColor: isViewMode ? '#f3f4f6' : '#f9fafb',
                    cursor: isViewMode ? 'not-allowed' : 'text'
                  }}
                />
              </Form.Item>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-900">
                Description <span className="text-red-500">*</span>
              </label>
              <Form.Item
                name="description"
                rules={[{ required: !isViewMode, message: 'Please enter description' }]}
                className="mb-0"
              >
                <TextArea
                  placeholder="e.g Transaction of the screen"
                  rows={3}
                  readOnly={isViewMode}
                  // disabled={isViewMode}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  style={{ 
                    backgroundColor: isViewMode ? '#f3f4f6' : '#f9fafb',
                    cursor: isViewMode ? 'not-allowed' : 'text'
                  }}
                />
              </Form.Item>
            </div>
          </div>
        </div>
      </Form>
    </CustomModal>
  );
};

export default AddPermissionModal;